/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import CheckNums from '../../../../../../core/checks/numbers/CheckNums.js';
import { CommandHandler } from '../../../../../../core/decorator/CommandHandlerDecorator.js';
import { lazyInject } from '../../../../../../core/decorator/LazyInjectDecorator.js';
import BigDecimal from '../../../../../../domain/context/shared/BigDecimal.js';
import { StableCoinNotAssociated } from '../../error/StableCoinNotAssociated.js';
import AccountService from '../../../../../service/AccountService.js';
import StableCoinService from '../../../../../service/StableCoinService.js';
import TransactionService from '../../../../../service/TransactionService.js';
import { DecimalsOverRange } from '../../error/DecimalsOverRange.js';
import { OperationNotAllowed } from '../../error/OperationNotAllowed.js';
import { CashInCommand, CashInCommandResponse } from './CashInCommand.js';
import { GetAccountTokenRelationshipQuery } from '../../../../query/account/tokenRelationship/GetAccountTokenRelationshipQuery.js';
import { FreezeStatus, KycStatus, } from '../../../../../../port/out/mirror/response/AccountTokenRelationViewModel.js';
import { RPCQueryAdapter } from '../../../../../../port/out/rpc/RPCQueryAdapter.js';
import { AccountFreeze } from '../../error/AccountFreeze.js';
import { AccountNotKyc } from '../../error/AccountNotKyc.js';
import { GetReserveAmountQuery } from '../../../../query/stablecoin/getReserveAmount/GetReserveAmountQuery.js';
import { RESERVE_DECIMALS } from '../../../../../../domain/context/reserve/Reserve.js';
import { MirrorNodeAdapter } from '../../../../../../port/out/mirror/MirrorNodeAdapter.js';
import { BigNumber } from 'ethers';
import { TokenSupplyType } from '@hashgraph/sdk';
const MAX_SUPPLY = 9223372036854775807n;
let CashInCommandHandler = class CashInCommandHandler {
    constructor(stableCoinService, accountService, transactionService, queryAdapter, mirrorNode) {
        this.stableCoinService = stableCoinService;
        this.accountService = accountService;
        this.transactionService = transactionService;
        this.queryAdapter = queryAdapter;
        this.mirrorNode = mirrorNode;
    }
    async execute(command) {
        const { amount, targetId, tokenId, startDate } = command;
        const handler = this.transactionService.getHandler();
        const account = this.accountService.getCurrentAccount();
        const tokenRelationship = (await this.stableCoinService.queryBus.execute(new GetAccountTokenRelationshipQuery(targetId, tokenId))).payload;
        if (!tokenRelationship) {
            throw new StableCoinNotAssociated(targetId.toString(), tokenId.toString());
        }
        if (tokenRelationship.freezeStatus === FreezeStatus.FROZEN) {
            throw new AccountFreeze(targetId.toString());
        }
        if (tokenRelationship.kycStatus === KycStatus.REVOKED) {
            throw new AccountNotKyc(targetId.toString());
        }
        const capabilities = await this.stableCoinService.getCapabilities(account, tokenId);
        const coin = capabilities.coin;
        if (CheckNums.hasMoreDecimals(amount, coin.decimals)) {
            throw new DecimalsOverRange(coin.decimals);
        }
        const amountBd = BigDecimal.fromString(amount, coin.decimals);
        if (!coin.maxSupply || !coin.totalSupply)
            throw new OperationNotAllowed(`The stablecoin is not valid`);
        if (coin.supplyType === TokenSupplyType.Finite.toString()) {
            if (coin.maxSupply &&
                coin.maxSupply.isGreaterThan(BigDecimal.ZERO) &&
                amountBd.isGreaterThan(coin.maxSupply.subUnsafe(coin.totalSupply))) {
                throw new OperationNotAllowed(`The total supply amount cannot be greater than the max supply (${coin.maxSupply}).`);
            }
        }
        else {
            const max = BigDecimal.fromValue(BigNumber.from(MAX_SUPPLY), coin.decimals, coin.decimals);
            if (amountBd.isGreaterThan(max.subUnsafe(coin.totalSupply))) {
                throw new OperationNotAllowed(`The total supply amount cannot be greater than the limit ${max}. You could check the limits here: https://docs.hedera.com/guides/docs/hedera-api/token-service/tokencreate`);
            }
        }
        if (coin.evmProxyAddress) {
            const reserveAddress = await this.queryAdapter.getReserveAddress(coin.evmProxyAddress);
            if (reserveAddress.toString() !== '0.0.0') {
                const totalAmount = amountBd.addUnsafe(coin.totalSupply);
                const commonDecimals = RESERVE_DECIMALS > coin.decimals
                    ? RESERVE_DECIMALS
                    : coin.decimals;
                const reserveAmountBd = (await this.stableCoinService.queryBus.execute(new GetReserveAmountQuery(tokenId))).payload;
                if (totalAmount
                    .setDecimals(commonDecimals)
                    .isGreaterThan(reserveAmountBd.setDecimals(commonDecimals))) {
                    throw new OperationNotAllowed(`The reserve is less than the amount to cash in (${amount})`);
                }
            }
        }
        const res = await handler.cashin(capabilities, targetId, amountBd, startDate);
        return Promise.resolve(new CashInCommandResponse(res.error === undefined));
    }
};
CashInCommandHandler = __decorate([
    CommandHandler(CashInCommand),
    __param(0, lazyInject(StableCoinService)),
    __param(1, lazyInject(AccountService)),
    __param(2, lazyInject(TransactionService)),
    __param(3, lazyInject(RPCQueryAdapter)),
    __param(4, lazyInject(MirrorNodeAdapter)),
    __metadata("design:paramtypes", [StableCoinService,
        AccountService,
        TransactionService,
        RPCQueryAdapter,
        MirrorNodeAdapter])
], CashInCommandHandler);
export { CashInCommandHandler };
